/**
 * BuildingConfigService — 활성 건물 BMS 포인트 매핑 설정 관리
 * ==============================================================
 * - 서버에서 현재 건물 설정을 로드하고 캐시
 * - SimulationService / BMSSimulationService가 이 서비스를 통해 포인트 ID를 조회
 * - subscribe()로 설정 변경 시 컴포넌트 재렌더링 지원
 */

import { AuthService } from './AuthService';

export interface ZoneConfig {
    id: string;
    name: string;
    tempPointId: string;
    co2PointId?: string | null;
    baseHumidity: number;
}

export interface FloorConfig {
    label: string;
    description: string;
    accent: string;
    zones: ZoneConfig[];
}

export interface EquipmentConfig {
    label: string;
    type: 'heat_pump' | 'chiller' | 'ahu' | 'boiler' | 'cooling_tower' | 'pump' | 'tank' | 'thermal_storage' | 'heat_exchanger' | 'fcu' | 'other';
    supplyPointId?: string | null;
    returnPointId?: string | null;
    primarySupplyPointId?: string | null;
    primaryReturnPointId?: string | null;
    secondarySupplyPointId?: string | null;
    secondaryReturnPointId?: string | null;
    raTemp?: string | null;
    saTemp?: string | null;
    oaTemp?: string | null;
    saFlow?: string | null;
    raFlow?: string | null;
    raHumidity?: string | null;
}

export interface FacilityProfileMetadata {
    facilityId: string;
    version: number;
    status: 'draft' | 'active' | 'archived' | string;
    createdAt: string;
    updatedAt: string;
    createdBy?: string | null;
    basedOnVersion?: number | null;
}

export interface FacilityValidation {
    valid: boolean;
    errors: string[];
    warnings: string[];
}

export interface FacilitySummary {
    facilityId: string;
    name: string;
    location: string;
    latestVersion: number;
    versionCount: number;
    active: boolean;
    activeVersion: number | null;
    updatedAt: string;
    source: string;
}

export interface FacilityVersionSummary {
    facilityId: string;
    version: number;
    status: string;
    createdAt: string;
    updatedAt: string;
    createdBy?: string | null;
    basedOnVersion?: number | null;
    topologySummary?: {
        nodes: number;
        edges: number;
        source: string;
        requiresReview: boolean;
        discovery?: {
            plantLoops?: number;
            condenserLoops?: number;
            airLoops?: number;
            zones?: number;
            conditionedZones?: number;
            plenums?: number;
            unresolved?: number;
        } | null;
    };
    validation: FacilityValidation;
}

export interface FacilityTemplateSummary {
    id: string;
    name: string;
    description: string;
    readOnly: boolean;
}

export interface FacilityTemplate extends FacilityTemplateSummary {
    profile: BuildingConfig;
}

export interface FacilityClassification {
    regionCode: string;
    regionLabel: string;
    facilityType: string;
    facilityTypeLabel: string;
    primaryUse: string;
    sequence: string;
}

export interface CanonicalPoint {
    id: string;
    sourcePointId: string;
    canonicalRole: string;
    label: string;
    unit: string;
    dataType: 'number' | 'boolean' | 'enum';
    access: 'read' | 'write' | 'read_write';
    protocol?: 'bacnet' | 'modbus' | 'opcua' | 'rest' | 'csv' | 'virtual' | string;
    equipmentId?: string | null;
    floorId?: string | null;
    spaceId?: string | null;
    min?: number | null;
    max?: number | null;
    sampleSeconds?: number | null;
    confidence?: number | null;
    qualityRules?: Record<string, unknown>;
}

export interface EquipmentNode {
    id: string;
    type: string;
    label: string;
    floorId?: string | null;
    spaceId?: string | null;
    template?: string;
    pointIds: string[];
    properties: Record<string, unknown> & { layout?: { x: number; y: number }; generated?: boolean };
}

export interface EquipmentEdge {
    id: string;
    from: string;
    to: string;
    relation: 'feeds' | 'returns_to' | 'bypasses' | 'serves' | 'contains' | 'meters' | 'controls';
    medium?: 'air' | 'thermal_service' | 'chilled_water' | 'hot_water' | 'condenser_water' | 'electricity' | 'refrigerant';
    operatingModes?: Array<'cooling' | 'heating'>;
    pipe?: {
        role: 'supply' | 'return' | 'bypass';
        label?: string;
        pointIds?: string[];
        nominalDiameterMm?: number | null;
        material?: string | null;
        insulation?: string | null;
    };
}

export interface EquipmentTopology {
    nodes: EquipmentNode[];
    edges: EquipmentEdge[];
    generatedAt?: string;
    source?: string;
    layoutVersion?: string;
}

export interface EnergyPlusTopologyAnalysis {
    energyplusVersion?: string | null;
    topologyProposal: EquipmentTopology;
    topologyDiscovery: {
        summary: {
            plantLoops: number;
            condenserLoops: number;
            airLoops: number;
            zones: number;
            conditionedZones?: number;
            plenums?: number;
            nodes: number;
            edges: number;
            unresolved: number;
        };
        warnings: string[];
        evidence: Array<Record<string, unknown>>;
    };
    validation: {
        valid: boolean;
        runtimeValidated: boolean;
        errors: string[];
        warnings: string[];
    };
}

export interface ModelSpaceBinding {
    id: string;
    modelUrn: string;
    modelGuid: string;
    objectId: number;
    externalId?: string | null;
    name: string;
    category?: string | null;
    level?: string | null;
    canonicalSpaceId?: string | null;
    confidence?: number;
    status?: 'matched' | 'review' | 'unmapped' | string;
}

export interface ModelEquipmentBinding extends ModelSpaceBinding {
    family?: string | null;
    type?: string | null;
    canonicalEquipmentId?: string | null;
}

export interface BuildingConfig {
    schemaVersion?: 2;
    profile?: FacilityProfileMetadata;
    meta: {
        id: string;
        name: string;
        location: string;
        floorArea_m2: number;
        floors: string[];
        baseline_kW: number;
        createdAt: string;
        source: string;
        classification?: FacilityClassification;
        weatherGrid?: {
            name: string;
            nx: number;
            ny: number;
        };
    };
    kpiPoints: {
        outdoorTemp?: string | null;
        outdoorHumidity?: string | null;
        indoorTemp?: string | null;
        indoorHumidity?: string | null;
        supplyAirTemp?: string | null;
        supplyAirFlow?: string | null;
        returnAirFlow?: string | null;
        co2Sensors: string[];
    };
    powerPoints: {
        estimationMethod: string;
        hpOutletPointIds: string[];
        chillerOutletPointId?: string | null;
        ahuFlowPointId?: string | null;
    };
    floors: Record<string, FloorConfig>;
    equipment: Record<string, EquipmentConfig>;
    models?: {
        revit?: { name?: string | null; urn?: string | null; sourceFile?: string | null; status?: string; modelGuid?: string | null; metadataImportedAt?: string | null };
        energyplus?: { idfPath?: string | null; epwPath?: string | null; energyplusVersion?: string | null; adapterId?: string | null; adapterVersion?: number | null; status?: string };
    };
    points?: Record<string, CanonicalPoint>;
    topology?: EquipmentTopology;
    bindings?: {
        spaces?: ModelSpaceBinding[];
        equipment?: ModelEquipmentBinding[];
    };
    control?: {
        deploymentStage?: 'monitoring' | 'advisory' | 'supervised' | 'autonomous' | string;
        writeEnabled?: boolean;
        safetyRules?: Array<Record<string, unknown>>;
        policyProfileId?: string | null;
    };
    dashboard?: {
        enabledModules?: string[];
        systemTemplates?: Record<string, unknown>;
        overrides?: Record<string, unknown>;
    };
}

// ── SEDC 기본값 (서버 없을 때 fallback) ────────────────────────────────────
const SEDC_FALLBACK: BuildingConfig = {
    meta: {
        id: 'SEDC-01', name: '서울에너지드림센터', location: '서울 상암동',
        floorArea_m2: 1200, floors: ['1F', '2F', '3F'], baseline_kW: 1580,
        createdAt: '2024-01-01T00:00:00Z', source: 'sedc_default',
    },
    kpiPoints: {
        outdoorTemp: 'DDC_G.D.D0100', outdoorHumidity: 'DDC_G.D.D0151',
        indoorTemp: 'AHU.D4001', indoorHumidity: 'AHU.D4000',
        supplyAirTemp: 'AHU.D4002', supplyAirFlow: 'AHU.D4012', returnAirFlow: 'AHU.D4013',
        co2Sensors: ['DDC_G.D.D0152','DDC_G.D.D0153','DDC_G.D.D0154','DDC_G.D.D0155','DDC_G.D.D0158','DDC_G.D.D0159','DDC_G.D.D0160'],
    },
    powerPoints: {
        estimationMethod: 'heuristic',
        hpOutletPointIds: ['DDC_G.D.D0104','DDC_G.D.D0112','DDC_G.D.D0121'],
        chillerOutletPointId: 'DDC_M.D.D0103', ahuFlowPointId: 'AHU.D4012',
    },
    floors: {
        '1F': { label: '1층', description: '출입 로비 · 전시홍보 공간', accent: 'cyan', zones: [
            { id: 'z-1f-lobby', name: '출입 로비',  tempPointId: 'DDC_M.D.D0123', co2PointId: 'DDC_G.D.D0152', baseHumidity: 52 },
            { id: 'z-1f-exh1',  name: '전시홍보 1', tempPointId: 'DDC_M.D.D0123', co2PointId: 'DDC_G.D.D0153', baseHumidity: 50 },
            { id: 'z-1f-exh2',  name: '전시홍보 2', tempPointId: 'DDC_M.D.D0123', co2PointId: 'DDC_G.D.D0154', baseHumidity: 49 },
            { id: 'z-1f-exh3',  name: '전시홍보 3', tempPointId: 'DDC_M.D.D0123', co2PointId: 'DDC_G.D.D0155', baseHumidity: 50 },
            { id: 'z-1f-wc',    name: '화장실',     tempPointId: 'DDC_M.D.D0123', co2PointId: 'DDC_G.D.D0156', baseHumidity: 65 },
        ]},
        '2F': { label: '2층', description: '전시홍보 · 전시창고', accent: 'violet', zones: [
            { id: 'z-2f-exh1',  name: '전시홍보 1', tempPointId: 'DDC_M.D.D0122', co2PointId: 'DDC_G.D.D0158', baseHumidity: 50 },
            { id: 'z-2f-exh2',  name: '전시홍보 2', tempPointId: 'DDC_M.D.D0122', co2PointId: 'DDC_G.D.D0159', baseHumidity: 49 },
            { id: 'z-2f-exh3',  name: '전시홍보 3', tempPointId: 'DDC_M.D.D0122', co2PointId: 'DDC_G.D.D0160', baseHumidity: 48 },
            { id: 'z-2f-store', name: '전시창고',   tempPointId: 'DDC_M.D.D0122', co2PointId: 'DDC_G.D.D0161', baseHumidity: 47 },
            { id: 'z-2f-wc',    name: '화장실',     tempPointId: 'DDC_M.D.D0122', co2PointId: 'DDC_G.D.D0162', baseHumidity: 63 },
        ]},
        '3F': { label: '3층', description: '다목적실 · 사무실 · 북카페', accent: 'amber', zones: [
            { id: 'z-3f-multi1',  name: '다목적실 1',  tempPointId: 'DDC_M.D.D0116', baseHumidity: 48 },
            { id: 'z-3f-multi2',  name: '다목적실 2',  tempPointId: 'DDC_M.D.D0117', baseHumidity: 48 },
            { id: 'z-3f-office',  name: '사무실',      tempPointId: 'DDC_M.D.D0118', baseHumidity: 46 },
            { id: 'z-3f-meeting', name: '회의실',      tempPointId: 'DDC_M.D.D0119', baseHumidity: 47 },
            { id: 'z-3f-exp',     name: '체험실',      tempPointId: 'DDC_M.D.D0120', baseHumidity: 50 },
            { id: 'z-3f-prep',    name: '체험준비실',  tempPointId: 'DDC_M.D.D0121', baseHumidity: 48 },
            { id: 'z-3f-cafe',    name: '북카페',      tempPointId: 'DDC_M.D.D0122', baseHumidity: 53 },
            { id: 'z-3f-lobby',   name: '로비',        tempPointId: 'DDC_M.D.D0123', baseHumidity: 50 },
        ]},
    },
    equipment: {
        hp1: { label: '지열히트펌프 1호기', type: 'heat_pump', supplyPointId: 'DDC_G.D.D0104', returnPointId: 'DDC_G.D.D0103', primarySupplyPointId: 'DDC_G.D.D0101', primaryReturnPointId: 'DDC_G.D.D0102' },
        hp2: { label: '지열히트펌프 2호기', type: 'heat_pump', supplyPointId: 'DDC_G.D.D0112', returnPointId: 'DDC_G.D.D0113', primarySupplyPointId: 'DDC_G.D.D0110', primaryReturnPointId: 'DDC_G.D.D0111' },
        hp3: { label: '지열히트펌프 3호기', type: 'heat_pump', supplyPointId: 'DDC_G.D.D0121', returnPointId: 'DDC_G.D.D0122', primarySupplyPointId: 'DDC_G.D.D0119', primaryReturnPointId: 'DDC_G.D.D0120' },
        chiller: { label: '냉동기', type: 'chiller', supplyPointId: 'DDC_M.D.D0103', returnPointId: 'DDC_M.D.D0102', secondarySupplyPointId: 'DDC_M.D.D0103', secondaryReturnPointId: 'DDC_M.D.D0102' },
        ahu: { label: '공조기 (AHU)', type: 'ahu', raTemp: 'AHU.D4001', raHumidity: 'AHU.D4000', saTemp: 'AHU.D4002', oaTemp: 'AHU.D4003', saFlow: 'AHU.D4012', raFlow: 'AHU.D4013' },
    },
};

// ── Service singleton ───────────────────────────────────────────────────────
export class BuildingConfigService {
    private static _config: BuildingConfig = SEDC_FALLBACK;
    private static _loaded = false;
    private static _subscribers: Array<() => void> = [];

    /** 서버에서 설정을 로드 (앱 시작 시 1회 호출) */
    static async load(): Promise<BuildingConfig> {
        try {
            const res = await fetch('/api/config/building');
            if (res.ok) {
                this._config = await res.json();
            }
        } catch (_) {
            // 서버 미연결 시 fallback 유지
        }
        this._loaded = true;
        this._notify();
        return this._config;
    }

    /** 현재 활성 설정 반환 */
    static get(): BuildingConfig {
        return this._config;
    }

    /** 설정이 로드되었는지 여부 */
    static isLoaded(): boolean {
        return this._loaded;
    }

    /** 설정 변경 구독 */
    static subscribe(fn: () => void): () => void {
        this._subscribers.push(fn);
        return () => { this._subscribers = this._subscribers.filter(s => s !== fn); };
    }

    /** AI 분석 요청 (저장하지 않음 — 미리보기용) */
    static async analyze(rawPointList: string, buildingName: string): Promise<{ config: BuildingConfig; warning?: string }> {
        const res = await fetch('/api/config/analyze', {
            method: 'POST',
            headers: { ...AuthService.authHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ rawPointList, buildingName }),
        });
        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.error || '분석 실패');
        }
        return res.json();
    }

    /** 설정 저장 및 즉시 적용 */
    static async save(config: BuildingConfig): Promise<void> {
        const res = await fetch('/api/config/building', {
            method: 'POST',
            headers: { ...AuthService.authHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify(config),
        });
        if (!res.ok) throw new Error('저장 실패');
        const data = await res.json();
        this._config = data.profile ?? config;
        this._notify();
    }

    /** 등록된 시설과 활성 버전 목록 */
    static async listFacilities(): Promise<{ facilities: FacilitySummary[]; active: { facilityId: string; version: number } }> {
        const res = await fetch('/api/config/facilities', { cache: 'no-store' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || '시설 목록 조회 실패');
        return data;
    }

    /** 제품에 포함된 읽기 전용 기준 시설 예시 목록 */
    static async listTemplates(): Promise<FacilityTemplateSummary[]> {
        const res = await fetch('/api/config/templates', { cache: 'no-store' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || '시설 기준 예시 목록 조회 실패');
        return data.templates ?? [];
    }

    /** 기준 예시는 직접 수정하지 않고 새 시설 초안의 복제 원본으로만 사용한다. */
    static async getTemplate(templateId: string): Promise<FacilityTemplate> {
        const res = await fetch(`/api/config/templates/${encodeURIComponent(templateId)}`, { cache: 'no-store' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || '시설 기준 예시 조회 실패');
        return data as FacilityTemplate;
    }

    /** 시설의 변경 이력 */
    static async listVersions(facilityId: string): Promise<FacilityVersionSummary[]> {
        const res = await fetch(`/api/config/facilities/${encodeURIComponent(facilityId)}/versions`, { cache: 'no-store' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || '시설 버전 조회 실패');
        return data.versions ?? [];
    }

    /** 시설의 최신 초안/활성 버전을 읽는다. 활성 시설 전환은 일어나지 않는다. */
    static async getLatest(facilityId: string): Promise<BuildingConfig> {
        const res = await fetch(`/api/config/facilities/${encodeURIComponent(facilityId)}`, { cache: 'no-store' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || '시설 프로필 조회 실패');
        return data as BuildingConfig;
    }

    /** 특정 시설 버전을 읽는다. */
    static async getVersion(facilityId: string, version: number): Promise<BuildingConfig> {
        const res = await fetch(`/api/config/facilities/${encodeURIComponent(facilityId)}/versions/${version}`, { cache: 'no-store' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || '시설 프로필 버전 조회 실패');
        return data as BuildingConfig;
    }

    /** 새 시설의 첫 버전을 초안으로만 만든다. */
    static async createDraft(config: BuildingConfig, createdBy = 'facility-studio'): Promise<{ profile: BuildingConfig; validation: FacilityValidation }> {
        const res = await fetch('/api/config/facilities', {
            method: 'POST',
            headers: { ...AuthService.authHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ profile: config, activate: false, createdBy }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || '새 시설 초안 생성 실패');
        return { profile: data.profile, validation: data.validation };
    }

    /** 저장 전 후보 또는 서버의 최신 시설 버전을 검증한다. */
    static async validate(facilityId: string, profile?: BuildingConfig): Promise<FacilityValidation> {
        const res = await fetch(`/api/config/facilities/${encodeURIComponent(facilityId)}/validate`, {
            method: 'POST',
            headers: { ...AuthService.authHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify(profile ? { profile } : {}),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok && !Array.isArray(data.errors)) throw new Error(data.error || '시설 프로필 검증 실패');
        return data as FacilityValidation;
    }

    /** IDF/EPW를 정적으로 분석하고, 활성 시설을 바꾸지 않는 계통도 초안을 생성한다. */
    static async analyzeEnergyPlusTopology(
        facilityId: string,
        idfPath: string,
        epwPath: string,
        adapterId?: string,
    ): Promise<EnergyPlusTopologyAnalysis> {
        const res = await fetch(`/api/config/facilities/${encodeURIComponent(facilityId)}/energyplus/analyze`, {
            method: 'POST',
            headers: { ...AuthService.authHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ idfPath, epwPath, adapterId, createdBy: 'facility-studio-discovery' }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'EnergyPlus 계통 분석 실패');
        return data.adapter as EnergyPlusTopologyAnalysis;
    }

    /** 검증을 통과한 과거/신규 버전을 활성화하고 모든 구독자에게 알림 */
    static async activate(facilityId: string, version: number): Promise<BuildingConfig> {
        const res = await fetch(`/api/config/facilities/${encodeURIComponent(facilityId)}/versions/${version}/activate`, {
            method: 'POST',
            headers: AuthService.authHeaders(),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || '시설 버전 활성화 실패');
        this._config = data.profile;
        this._notify();
        return this._config;
    }

    /** 활성 상태가 아닌 개별 시설 버전을 영구 삭제 */
    static async deleteVersion(facilityId: string, version: number): Promise<void> {
        const res = await fetch(`/api/config/facilities/${encodeURIComponent(facilityId)}/versions/${version}`, {
            method: 'DELETE',
            headers: AuthService.authHeaders(),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || '시설 버전 삭제 실패');
    }

    /** 활성 상태가 아닌 시설과 그 프로필 버전을 영구 삭제 */
    static async deleteFacility(facilityId: string): Promise<void> {
        const res = await fetch(`/api/config/facilities/${encodeURIComponent(facilityId)}`, {
            method: 'DELETE',
            headers: AuthService.authHeaders(),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || '시설 삭제 실패');
    }

    /** 현재 시설 프로필을 덮어쓰지 않고 다음 초안 버전으로 저장 */
    static async saveDraft(config: BuildingConfig, createdBy = 'settings-ui'): Promise<{ profile: BuildingConfig; validation: FacilityValidation }> {
        const facilityId = config.profile?.facilityId || config.meta.id;
        const res = await fetch(`/api/config/facilities/${encodeURIComponent(facilityId)}/versions`, {
            method: 'POST',
            headers: { ...AuthService.authHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ profile: config, activate: false, createdBy }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || '시설 프로필 초안 저장 실패');
        return { profile: data.profile, validation: data.validation };
    }

    /** SEDC 기본값으로 초기화 */
    static async reset(): Promise<BuildingConfig> {
        const res = await fetch('/api/config/reset', { method: 'POST', headers: AuthService.authHeaders() });
        const data = await res.json();
        this._config = data.config;
        this._notify();
        return this._config;
    }

    private static _notify() {
        this._subscribers.forEach(fn => fn());
    }
}
